import { createContext, useContext, type ComponentType, type ReactNode } from 'react';
import type { CanvasBinding } from './types/binding';
import type { YjsRoomKind } from '@/lib/sync/yjs-registry';

/**
 * Shape of the read-only canvas preview component. Used by editor's
 * entity-preview-card (and any other generic preview surface) to render a
 * canvas entity without depending on `features/canvas/*` directly — the
 * canvas-feature registers its renderer through `CanvasPreviewProvider` at
 * the app/ layer.
 *
 * Lives in `lib/canvas/` so both editor (consumer) and canvas (producer) can
 * import the type without cross-feature imports.
 *
 * `binding` (optional, top-level by default) lets a non-canvas feature render
 * a NESTED canvas — the canvas's Yjs roots living under a parent map in the
 * host doc — purely through this injection seam, with no cross-feature import.
 *
 * `entityKindHint` (optional, `'canvas'` by default) selects WHICH Yjs room to
 * resolve. A nested canvas in a non-canvas entity's doc (e.g. a
 * document-surfaces diagram) must resolve the HOST entity's room
 * (`'document'`), not a canvas room. `binding` says WHERE in the doc the
 * roots live; `entityKindHint` says WHICH doc/room to subscribe to. Both are
 * needed for an embedded canvas.
 *
 * `nested` (optional) is the cross-feature-safe way for a host feature (e.g.
 * `features/document-surfaces`) to request the standard nested-canvas binding
 * WITHOUT importing `nestedBinding` from `features/canvas` (an ESLint zone
 * error). When `nested` is true, the registered renderer constructs the
 * nested binding internally. Mutually exclusive with `binding`; `nested`
 * wins if both are set.
 *
 * `nestedKey` (optional, default `'canvas'`) is the parent map key `nested`
 * nests under. A host that can embed more than one canvas in the same doc —
 * several canvas surfaces on one document — must pass a key unique per
 * embed, or every embed resolves the same roots and editing one changes the
 * others. The default keeps every existing caller byte-identical.
 *
 * `readOnly` (optional, default `true`) — the preview is view-only by default
 * (the registered renderer mounts CanvasViewer). A host feature that has
 * resolved EDIT permission passes `readOnly={false}` to get the interactive
 * CanvasEditor mount instead (writing through the same nested binding + host
 * room). Permission is the host's responsibility, exactly as a standalone
 * canvas gates `CanvasEditor` via `usePermission`.
 */
export type CanvasPreviewRenderer = ComponentType<{
  entityId: string;
  onStats?: (stats: { nodeCount: number }) => void;
  binding?: CanvasBinding;
  entityKindHint?: YjsRoomKind;
  nested?: boolean;
  nestedKey?: string;
  readOnly?: boolean;
}>;

const CanvasPreviewContext = createContext<CanvasPreviewRenderer | null>(null);

interface CanvasPreviewProviderProps {
  renderer: CanvasPreviewRenderer;
  children: ReactNode;
}

export function CanvasPreviewProvider({ renderer, children }: CanvasPreviewProviderProps) {
  return (
    <CanvasPreviewContext.Provider value={renderer}>
      {children}
    </CanvasPreviewContext.Provider>
  );
}

/**
 * Returns the registered canvas preview renderer, or `null` when no provider
 * is mounted. Callers should fall back to a non-canvas rendering path when
 * the renderer is unavailable.
 */
export function useCanvasPreviewRenderer(): CanvasPreviewRenderer | null {
  return useContext(CanvasPreviewContext);
}
